import { Component, Input, OnInit, OnDestroy, Optional } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { TranslationService } from 'src/app/common-services/translation.service';
import { Subscription } from 'rxjs';
import { DescriptionModalComponent } from './description-modal.component';

@Component({
  selector: 'app-expandable-text',
  templateUrl: './expandable-text.component.html',
  styleUrls: ['./expandable-text.component.css']
})
export class ExpandableTextComponent implements OnInit, OnDestroy {
  @Input() text: string = '';
  @Input() limit: number = 140;
  @Input() mode: 'inline' | 'modal' | 'both' = 'inline';
  @Input() dialogTitle: string = 'Work Description';
  @Input() fallback: string = '-';

  isExpanded: boolean = false;
  browserLang: string = 'en-US';
  private langSub?: Subscription;

  constructor(
    private dialog: MatDialog,
    @Optional() private translate?: TranslateService,
    @Optional() private translationService?: TranslationService
  ) {}

  ngOnInit(): void {
    this.browserLang = localStorage.getItem('lang') || 'en-US';
    if (this.translationService && this.translationService.selectLang) {
      this.langSub = this.translationService.selectLang.subscribe((lang: any) => {
        if (lang) {
          this.browserLang = lang;
        }
      });
    } else if (this.translate && this.translate.onLangChange) {
      this.langSub = this.translate.onLangChange.subscribe((event: any) => {
        if (event && event.lang) {
          this.browserLang = event.lang;
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.langSub) {
      this.langSub.unsubscribe();
    }
  }

  get currentLang(): string {
    return this.browserLang || localStorage.getItem('lang') || 'en-US';
  }

  get showMoreText(): string {
    if (this.translate) {
      const val = this.translate.instant('common.showMore');
      if (val && val !== 'common.showMore' && !val.startsWith('common.')) return val;
    }
    const dict: Record<string, string> = {
      'en-US': 'Show More',
      'hi-IN': 'शो मोर',
      'mr-IN': 'शो मोर',
      'bn-IN': 'শো মোর'
    };
    return dict[this.currentLang] || 'Show More';
  }

  get showLessText(): string {
    if (this.translate) {
      const val = this.translate.instant('common.showLess');
      if (val && val !== 'common.showLess' && !val.startsWith('common.')) return val;
    }
    const dict: Record<string, string> = {
      'en-US': 'Show Less',
      'hi-IN': 'शो लेस',
      'mr-IN': 'शो लेस',
      'bn-IN': 'শো লেস'
    };
    return dict[this.currentLang] || 'Show Less';
  }

  get viewFullDescriptionText(): string {
    if (this.translate) {
      const val = this.translate.instant('common.viewFullDescription');
      if (val && val !== 'common.viewFullDescription' && !val.startsWith('common.')) return val;
    }
    const dict: Record<string, string> = {
      'en-US': 'View Full Description',
      'hi-IN': 'व्यू फुल डिस्क्रिप्शन',
      'mr-IN': 'व्ह्यू फुल डिस्क्रिप्शन',
      'bn-IN': 'ভিউ ফুল ডেসক্রিপশন'
    };
    return dict[this.currentLang] || 'View Full Description';
  }

  get isLong(): boolean {
    return !!this.text && this.text.toString().trim().length > this.limit;
  }

  get truncatedText(): string {
    if (!this.text) return '';
    return this.text.toString().slice(0, this.limit);
  }

  get displayText(): string {
    if (!this.text || !this.text.toString().trim()) {
      return this.fallback;
    }
    return this.text.toString();
  }

  toggleExpand(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.isExpanded = !this.isExpanded;
  }

  openModal(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.dialog.open(DescriptionModalComponent, {
      width: '600px',
      maxWidth: '92vw',
      autoFocus: false,
      data: {
        title: this.dialogTitle,
        text: this.text
      }
    });
  }
}
